import { useState } from 'react';
import { Button, Checkbox } from '@moneyskyline/ui';
import type { AskCommitInput } from '@/api/hooks/useAskCommit';
import type { AskDraft } from '@/api/hooks/askSchemas';

type CommitFields = Omit<AskCommitInput, 'draft_id' | 'operation_id'>;

export function AskCommitForm({
  draft,
  acceptedIds,
  pending,
  onBack,
  onSave,
}: {
  draft: AskDraft;
  acceptedIds: string[];
  pending?: boolean;
  onBack: () => void;
  onSave: (input: CommitFields) => void;
}) {
  const supported = draft.supported_outcomes;
  const [outcome, setOutcome] = useState<CommitFields['outcome']>(supported[0] ?? 'event');
  const [eventName, setEventName] = useState(draft.interpretation?.event_name ?? '');
  const [eventKind, setEventKind] = useState<'trip' | 'project' | 'other'>(draft.interpretation?.suggested_event?.kind ?? 'other');
  const [tagBusiness, setTagBusiness] = useState(draft.interpretation?.suggested_event?.tag_business ?? false);
  const [conditionName, setConditionName] = useState(draft.interpretation?.condition_summary?.name ?? eventName);
  const [conditionEnabled, setConditionEnabled] = useState(false);
  const [acknowledgePartialSource, setAcknowledgePartialSource] = useState(!draft.coverage.partial_source);
  const window = draft.interpretation?.window;
  const suggestedEvent = draft.interpretation?.suggested_event;
  const eventStart = window?.start ?? suggestedEvent?.start_date;
  const eventEnd = window?.end ?? suggestedEvent?.end_date;
  const needsEvent = outcome === 'event' || outcome === 'event_and_condition';
  const needsCondition = outcome === 'condition' || outcome === 'event_and_condition';
  const canSave = (!needsEvent || (Boolean(eventName.trim()) && Boolean(eventStart) && Boolean(eventEnd))) && (!needsCondition || Boolean(conditionName.trim())) && acknowledgePartialSource;

  function save() {
    if (!canSave) return;
    onSave({
      outcome,
      accept_ids: acceptedIds,
      event: needsEvent && eventStart && eventEnd ? { name: eventName.trim(), kind: eventKind, start_date: eventStart, end_date: eventEnd, location: draft.interpretation?.location ?? null, tag_business: tagBusiness } : null,
      condition: needsCondition ? { name: conditionName.trim(), enabled: conditionEnabled } : null,
      acknowledge_partial_source: acknowledgePartialSource,
    });
  }

  return (
    <div className="flex flex-col gap-3" data-testid="ask-commit-form">
      <h2 className="text-lg font-semibold">Choose what to save</h2>
      <label className="flex flex-col gap-1 text-sm"><span className="text-xs text-muted-foreground">Outcome</span><select value={outcome} onChange={(event) => setOutcome(event.target.value as CommitFields['outcome'])} className="min-h-11 rounded-md border border-input px-3" aria-label="Ask outcome">{supported.map((choice) => <option key={choice} value={choice}>{choice === 'event_and_condition' ? 'Event and condition' : choice[0].toUpperCase() + choice.slice(1)}</option>)}</select></label>
      {needsEvent && <>
        <label className="flex flex-col gap-1 text-sm"><span className="text-xs text-muted-foreground">Event name</span><input value={eventName} onChange={(event) => setEventName(event.target.value)} className="min-h-11 rounded-md border border-input px-3" aria-label="Event name" /></label>
        <label className="flex flex-col gap-1 text-sm"><span className="text-xs text-muted-foreground">Event type</span><select value={eventKind} onChange={(event) => setEventKind(event.target.value as typeof eventKind)} className="min-h-11 rounded-md border border-input px-3"><option value="trip">Trip</option><option value="project">Project</option><option value="other">Other</option></select></label>
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={tagBusiness} onCheckedChange={(checked) => setTagBusiness(checked === true)} />Tag selected items as business</label>
      </>}
      {supported.includes('event_and_condition') && (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={needsCondition}
            onCheckedChange={(checked) => setOutcome(checked === true ? 'event_and_condition' : 'event')}
          />
          Also save a standing condition
        </label>
      )}
      {needsCondition && <>
        <label className="flex flex-col gap-1 text-sm"><span className="text-xs text-muted-foreground">Condition name</span><input value={conditionName} onChange={(event) => setConditionName(event.target.value)} className="min-h-11 rounded-md border border-input px-3" aria-label="Condition name" /></label>
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={conditionEnabled} onCheckedChange={(checked) => setConditionEnabled(checked === true)} />Enable this condition now</label>
      </>}
      {draft.coverage.partial_source && <label className="flex items-start gap-2 text-sm"><Checkbox checked={acknowledgePartialSource} onCheckedChange={(checked) => setAcknowledgePartialSource(checked === true)} /><span>Some sources are incomplete. I understand this applies only to the reviewed coverage.</span></label>}
      <p className="text-sm text-muted-foreground">{acceptedIds.length} item{acceptedIds.length === 1 ? '' : 's'} selected.</p>
      <div className="flex gap-2"><Button type="button" className="flex-1" disabled={pending || !canSave} onClick={save}>{needsEvent ? 'Save event' : 'Save'}</Button><Button type="button" variant="outline" onClick={onBack}>Back</Button></div>
    </div>
  );
}

export default AskCommitForm;
